import {
    useState,
    useReducer,
    useEffect,
    SyntheticEvent,
    useRef,
    KeyboardEvent,
    useMemo,
    useCallback,
    startTransition,
    useDeferredValue,
} from 'react';
import localforage from 'localforage';
import dayjs from 'dayjs';
import { useLocation, useOutletContext } from 'react-router-dom';
import { Box, CircularProgress, FormGroup, FormLabel, Typography, RadioGroup, Radio, Grid } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import {
    GridColDef,
    GridActionsCellItem,
    GridPaginationModel,
    GridRowSelectionModel,
    GridColumnVisibilityModel,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridRenderCellParams,
    GridSortModel,
} from '@mui/x-data-grid';
import { FormControl } from '@mui/material';
import { deleteDevicesAPI } from '@/api/storeManagement/device';
import { removeAPorRTDevicesAPI } from '@/api/storeManagement/device';
import { Visibility, Delete, LinkOff, Refresh, FilterList, SmartScreen, Lightbulb } from '@mui/icons-material';
import BaseIconButtonDevice from '@/components/ui/BaseIconButtonDevice';
import BaseCard from '@/components/ui/BaseCard';
import BaseDataTable from '@/components/ui/BaseDataTable';
import BaseTextField from '@/components/form/BaseTextField';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import BaseDialog from '@/components/ui/BaseDialog';
import BaseSelect from '@/components/form/BaseSelect';
import StoreFilterBlock from '@/components/store/StoreFilterBlock';
import NoStoreSelectionBlock from '@/components/store/NoStoreSelectionBlock';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import BaseTableColumn from '@/components/ui/BaseTableColumn';
import BaseTableColumnContent from '@/components/ui/BaseTableColumnContent';
import { useAppContext } from '@/provider/contexts';
import type { GridRowId } from '@mui/x-data-grid';
import {
    getDeviceDetail,
    fetchDeviceList,
    deassociateSingleDeviceAPI,
    deassociateMultipleDevices,
    refreshDevicesAPI,
    setDevicesToLedMode,
} from '@/api/storeManagement/device';
import {
    DevicePageState,
    DevicePageAction,
    DeviceItem,
    SortModelType,
    DeviceDetail,
    DeviceQuery,
} from '@/types/pages/storeManagement/device';
import '@/styles/pages/StoreMgmt/device/StoreDevicePage.scss';
import PopoverMenu from '@/components/ui/PopoverMenu';
import DateTimeRangePicker from '@/components/form/DateTimeRangePicker';
import { utcDateStringToLocalDateFormat } from '@/utils/formatUtils';
import React from 'react';
import { isEmpty, uniq } from 'lodash';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import PushImageDialog from '@/components/device/PushImageDialog';
import { pushImageToDevicesAPI } from '@/api/storeManagement/device';
import { AssociationProvider } from '@/components/associate/provider/Provider';
import AssociateDialog from '@/components/associate/AssociateDialog';
import { ASSOCIATION_MODE, AssociationMode } from '@/types/pages/storeManagement/device/associate';
import BaseFontIcon from '@/components/ui/BaseFontIcon';
import APDialog from './APDialog';
import { infraTaskAPI } from '@/api/storeManagement/registration';
import { StoreMgmtOutletContext } from '@/types/pages/storeManagement/context';
import DeviceDetailDialog from '@/components/device/DeviceDetailDialog';
import { isServiceUnavailable, SERVICE_UNAVAILABLE_MESSAGE } from '@/utils/deviceActionUtils';

const AP_SWITCH_CHANNEL_POLL_INTERVAL_MS = 3000;

function reducer(state: DevicePageState, action: DevicePageAction): DevicePageState {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, loading: action.payload };
        case 'SET_STATUS_DATE_TIME_PICKER_AND_QUERY':
            return {
                ...state,
                statusDateTimePickerData: {
                    ...state.statusDateTimePickerData,
                    startTime: action.payload.startTime,
                    endTime: action.payload.endTime,
                },
            };
        case 'SET_UPDATE_DATE_TIME_PICKER_AND_QUERY':
            return {
                ...state,
                updateDateTimePickerData: {
                    ...state.updateDateTimePickerData,
                    startTime: action.payload.startTime,
                    endTime: action.payload.endTime,
                },
            };
        case 'SET_SEARCH_QUERY':
            return { ...state, searchQuery: action.payload };
        case 'SET_SORT_MODEL':
            return {
                ...state,
                sortModel: action.payload,
            };
        case 'SET_DEVICES':
            return {
                ...state,
                devices: action.payload.aaData,
                rowCount: action.payload.total_records,
            };
        case 'SET_PRODUCT_ASSOCIATION_DISABLED':
            return {
                ...state,
                isProductAssociationDisabled: action.payload,
            };
        case 'SET_PARENT_MAC_DISABLED':
            return {
                ...state,
                isParentMacDisabled: action.payload,
            };
        case 'SET_PAGINATION_MODEL':
            return { ...state, paginationModel: action.payload };
        case 'SET_BUTTONS_STATUS':
            return { ...state, buttonsStatus: action.payload };
        default:
            return state;
    }
}
const statusFilterOptions = ['OFFLINE', 'ONLINE', 'RECONNECTION'];
const typeFilterOptions = [
    { label: 'AP', value: 2 },
    { label: 'RT', value: 1 },
    { label: 'TAG', value: 3 },
];

const availabilityOptions = [
    { value: 'All', label: 'All' },
    { value: '1', label: 'Available' },
    { value: '0', label: 'Not Available' },
];

const JobStatusMap = {
    All: '',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
    PROCESSING: 'PROCESSING',
};

const getJobStatusOptions = (t: (key: string) => string) => {
    return Object.entries(JobStatusMap).map(([key, value]) => ({
        value,
        label: t(key),
    }));
};

const ALL_VALUE = '';
const CONCRETE_STATUSES = ['Success', 'Failed', 'Processing'] as const;

type JobStatus = (typeof CONCRETE_STATUSES)[number] | typeof ALL_VALUE;

const normalizeJobStatuses = (input: string[]): string => {
    if (input.length === 0) {
        return ALL_VALUE;
    }

    const lastSelected = input[input.length - 1];
    const set = new Set(input);

    if (lastSelected === ALL_VALUE) {
        return ALL_VALUE;
    }

    set.delete(ALL_VALUE);

    const concrete = Array.from(set) as JobStatus[];

    if (concrete.length === CONCRETE_STATUSES.length) {
        return ALL_VALUE;
    }

    return concrete.join(',');
};

const defaultState: DevicePageState = {
    loading: true,
    statusDateTimePickerData: {
        startTime: null,
        endTime: null,
    },
    updateDateTimePickerData: {
        startTime: null,
        endTime: null,
    },
    searchQuery: {
        f_mac: '',
        f_product: '',
        f_parent_mac: '',
        f_profile: '',
        f_status_time: '',
        f_last_job_status: '',
        f_update_time: '',
        f_battery_lv: '',
        f_rssi: '',
        f_fw_version: '',
        f_layout: '',
        f_store: '',
        f_confirmed: 1,
        f_device_type: '',
        f_ed_status: '',
        f_in_rfu: '',
        f_whitelist: 'All',
    },
    sortModel: {} as SortModelType,
    isProductAssociationDisabled: false,
    isParentMacDisabled: false,
    devices: [],
    rowCount: 0,
    buttonsStatus: {
        associate: false,
        multiAssociate: false,
        deassociate: false,
        refresh: false,
        sendImage: false,
        remove: false,
        led: false,
    },
    paginationModel: {
        page: 0,
        pageSize: 5,
    },
};

type DeviceTypeName = 'ap' | 'rt' | 'tag';
type SearchField = keyof DevicePageState['searchQuery'];

type DeviceMeta = {
    mac: string;
    type: DeviceTypeName;
    hasDetails: boolean;
};

function toDeviceTypeName(name: string): DeviceTypeName {
    const v = name.toLowerCase();
    if (v === 'ap' || v === 'rt' || v === 'tag') return v;
    return 'tag';
}

type ButtonsStatus = {
    associate: boolean;
    multiAssociate: boolean;
    deassociate: boolean;
    refresh: boolean;
    sendImage: boolean;
    remove: boolean;
    led: boolean;
};

function deriveButtonsStatusFromMeta(
    selectedIds: readonly GridRowId[],
    macMeta: Map<string, DeviceMeta>,
): ButtonsStatus {
    if (selectedIds.length === 0) {
        return {
            associate: false,
            deassociate: false,
            multiAssociate: false,
            refresh: false,
            sendImage: false,
            remove: false,
            led: false,
        };
    }

    let hasAPorRT = false;
    let hasTag = false;

    for (const id of selectedIds) {
        const macKey = String(id);
        const meta = macMeta.get(macKey);
        if (!meta) continue;
        if (meta.type === 'ap' || meta.type === 'rt') hasAPorRT = true;
        if (meta.type === 'tag') hasTag = true;
        if (hasAPorRT && hasTag) break;
    }

    if (hasAPorRT) {
        return {
            associate: false,
            deassociate: false,
            refresh: false,
            sendImage: false,
            remove: false,
            led: false,
            multiAssociate: false,
        };
    }
    return {
        associate: true,
        deassociate: true,
        refresh: true,
        sendImage: true,
        remove: true,
        led: true,
        multiAssociate: true,
    };
}

export default function StoreDevicePage() {
    // const [imageError, setImageError] = useState(false);
    const { t } = useTranslation('common');
    const location = useLocation();
    const {
        isStoreReady,
        isTimezoneReady,
        storeDetailsError,
        storeSettings,
        formatUtcToStoreTime,
        storeWallTimeToUtcTimestamp,
    } = useOutletContext<StoreMgmtOutletContext>();
    const [state, dispatch] = useReducer(reducer, defaultState);
    const { manageStoreId, setNotification } = useAppContext();
    const { from } = location.state || {};
    const [isPushing, setIsPushing] = useState(false);
    const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
    const [quickActionStatus, setQuickActionStatus] = useState<{ key: string | null; status: 'idle' | 'loading' }>({
        key: null,
        status: 'idle',
    });
    const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false);
    const [associationMode, setAssociationMode] = useState<AssociationMode>(null);
    const [alertState, setAlertState] = useState<{
        message: string;
        severity: 'error' | 'warning' | 'info' | 'success';
        scope: 'quick' | 'advanced';
    } | null>(null);
    const [isDeassociateDialogOpen, setIsDeassociateDialogOpen] = useState(false);
    const [isDeassociating, setIsDeassociating] = useState(false);
    const [isApManagementOpen, setIsApManagementOpen] = useState(false);
    const [apSwitchChannelModeFromDetail, setApSwitchChannelModeFromDetail] = useState<
        'ENABLE' | 'DISABLE' | 'PROCESSING' | null
    >(null);
    const [apDestinationSubChannel, setApDestinationSubChannel] = useState<number | null>(null);
    const [deviceId, setDeviceId] = useState('');
    const [viewType, setViewType] = useState<'ap' | 'rt' | 'tag' | ''>('');
    const [isPushImageDialogOpen, setIsPushImageDialogOpen] = useState(false);
    const [deviceDetailDialogOpen, setDeviceDetailDialogOpen] = useState(false);
    const [isLedDialogOpen, setIsLedDialogOpen] = useState(false);
    const [ledMode, setLedMode] = useState('on');
    const [word1, setWord1] = useState('');
    const [word2, setWord2] = useState('');
    const [word3, setWord3] = useState('');
    const word1Ref = useRef<HTMLInputElement>(null);

    const [searchForm, setSearchForm] = useState<DevicePageState['searchQuery']>(state.searchQuery);
    const [statusTimeLocal, setStatusTimeLocal] = useState<{
        startTime: dayjs.Dayjs | null;
        endTime: dayjs.Dayjs | null;
    }>({
        startTime: state.statusDateTimePickerData.startTime,
        endTime: state.statusDateTimePickerData.endTime,
    });
    const [updateTimeLocal, setUpdateTimeLocal] = useState<{
        startTime: dayjs.Dayjs | null;
        endTime: dayjs.Dayjs | null;
    }>({
        startTime: state.updateDateTimePickerData.startTime,
        endTime: state.updateDateTimePickerData.endTime,
    });

    const searchFormRef = useRef(searchForm);
    searchFormRef.current = searchForm;
    const fetchAbortControllerRef = useRef<AbortController | null>(null);
    const latestFetchIdRef = useRef(0);

    const [showLedButton, setShowLedButton] = useState(false);
    const apManagementPollIntervalRef = useRef<number | null>(null);

    const assetFeatureRef = useRef(false);

    useEffect(() => {
        setSearchForm(state.searchQuery);
        setStatusTimeLocal({
            startTime: state.statusDateTimePickerData.startTime,
            endTime: state.statusDateTimePickerData.endTime,
        });
        setUpdateTimeLocal({
            startTime: state.updateDateTimePickerData.startTime,
            endTime: state.updateDateTimePickerData.endTime,
        });
    }, [state.searchQuery, state.statusDateTimePickerData, state.updateDateTimePickerData]);

    const updateSearchForm = useCallback((field: SearchField, value: string | string[]) => {
        setSearchForm((prev) => {
            const updated = { ...prev, [field]: value };
            searchFormRef.current = updated;
            return updated;
        });
    }, []);

    const macMetaMap = React.useMemo(() => {
        const map = new Map<string, DeviceMeta>();
        for (const d of state.devices) {
            const mac = String(d.mac ?? '');
            if (!mac) continue;
            map.set(mac, {
                mac,
                type: toDeviceTypeName(String(d.device_type_name ?? '')),
                hasDetails: Array.isArray(d.details) && d.details.length > 0,
            });
        }
        return map;
    }, [state.devices]);

    const devicesByMac = useMemo(() => {
        const map = new Map<string, DeviceItem>();
        state.devices.forEach((device) => {
            const mac = String(device.mac ?? '');
            if (mac) {
                map.set(mac, device);
            }
        });
        return map;
    }, [state.devices]);

    const deferredSelectedRows = useDeferredValue(selectedRows);

    const selectedDevices = useMemo(() => {
        const list: DeviceItem[] = [];
        deferredSelectedRows.forEach((rowId) => {
            const device = devicesByMac.get(String(rowId));
            if (device) {
                list.push(device);
            }
        });
        return list;
    }, [devicesByMac, deferredSelectedRows]);
    const selectedApDevices = useMemo(
        () => selectedDevices.filter((d) => (d.device_type_name ?? '').toLowerCase() === 'ap'),
        [selectedDevices],
    );

    const assetFilterDisable = useMemo(() => {
        const { devices } = state;
        return devices.length > 0 && devices[0].asset_flag === 2;
    }, [state]);

    const isNALD = useMemo(() => selectedDevices.some((device) => device.asset_flag === 0), [selectedDevices]);
    const isAllApSelected = useMemo(
        () =>
            selectedDevices.length > 0 &&
            selectedDevices.every((device) => (device.device_type_name ?? '').toLowerCase() === 'ap'),
        [selectedDevices],
    );
    const selectedApCount = useMemo(() => selectedApDevices.length, [selectedApDevices]);
    const apSwitchChannelMode = useMemo(() => {
        if (!isAllApSelected || selectedApDevices.length === 0) return null;
        const modes = selectedApDevices
            .map((d) => (d.switch_channel_mode ?? '').toString().toUpperCase())
            .filter(Boolean);
        if (modes.length === 0) return null;
        if (modes.every((m) => m === 'ENABLE')) return 'ENABLE';
        if (modes.every((m) => m === 'DISABLE')) return 'DISABLE';
        if (modes.every((m) => m === 'PROCESSING')) return 'PROCESSING';
        return null;
    }, [isAllApSelected, selectedApDevices]);
    const apSubChannelConsensus = useMemo(() => {
        if (!isAllApSelected || selectedApCount < 2) return { duplicateValues: [] as number[] };
        const channels = selectedApDevices
            .map((d) => d.current_sub_channel)
            .filter((v): v is number => v !== null && v !== undefined);
        if (!channels.length) return { duplicateValues: [] as number[] };

        const counts = channels.reduce<Record<number, number>>((acc, curr) => {
            acc[curr] = (acc[curr] || 0) + 1;
            return acc;
        }, {});

        const duplicateValues = Object.entries(counts)
            .filter(([, cnt]) => cnt > 1)
            .map(([key]) => Number(key));

        return { duplicateValues };
    }, [isAllApSelected, selectedApCount, selectedApDevices]);

    const stopApManagementPolling = useCallback(() => {
        if (apManagementPollIntervalRef.current !== null) {
            window.clearInterval(apManagementPollIntervalRef.current);
            apManagementPollIntervalRef.current = null;
        }
    }, []);
    const jobStatusOptions = useMemo(() => getJobStatusOptions(t), [t]);

    const deriveQueryPayload = (effectiveQuery: DeviceQuery): Partial<DeviceQuery> => {
        const payload: Record<string, unknown> = {};
        Object.entries(effectiveQuery).forEach(([key, value]) => {
            if (value === '') return;
            if (value === undefined || value === null) return;

            payload[key] = value;
        });

        if (payload['f_whitelist'] === 'All') {
            delete payload['f_whitelist'];
        }

        return payload;
    };

    const fetchDevices = useCallback(
        async (queryOverride?: DevicePageState['searchQuery']) => {
            setSelectedRows([]);
            dispatch({
                type: 'SET_BUTTONS_STATUS',
                payload: {
                    associate: false,
                    deassociate: false,
                    refresh: false,
                    sendImage: false,
                    remove: false,
                    led: false,
                    multiAssociate: false,
                },
            });
            fetchAbortControllerRef.current?.abort();
            if (!manageStoreId) return;

            const fetchId = latestFetchIdRef.current + 1;
            latestFetchIdRef.current = fetchId;

            const abortController = new AbortController();
            fetchAbortControllerRef.current = abortController;
            dispatch({ type: 'SET_LOADING', payload: true });

            try {
                const corporationId = (await localforage.getItem('corporation_id')) as number;
                const { field, sort } = state.sortModel;
                const effectiveQuery = queryOverride ?? state.searchQuery;

                const queryPayload = {
                    ...deriveQueryPayload(effectiveQuery),
                    f_store: manageStoreId,
                    data_per_page: state.paginationModel.pageSize,
                    current_page: state.paginationModel.page + 1,
                    sort_field: field,
                    sort_order: sort,
                };
                if (state.isProductAssociationDisabled) {
                    queryPayload.f_product = '!!NO_ASSOCIATION';
                }
                if (state.isParentMacDisabled) {
                    queryPayload.f_parent_mac = '!!NO_PARENT';
                }

                const {
                    data: { data, isSuccess, message },
                } = await fetchDeviceList(corporationId, queryPayload, { signal: abortController.signal });

                if (latestFetchIdRef.current !== fetchId) return;
                if (isSuccess) {
                    const devices = data.aaData || [];
                    const sortDetailsByPageIndex = (details: DeviceItem['details']) => {
                        return [...details].sort((a, b) => a.page_idx - b.page_idx);
                    };

                    const mapDeviceToRow = (device: DeviceItem): DeviceItem => {
                        return {
                            ...device,
                            ed_status_time: formatUtcToStoreTime(device.ed_status_time),
                            details: sortDetailsByPageIndex(device.details).map((detail) => ({
                                ...detail,
                                last_job_status_time: formatUtcToStoreTime(detail.last_job_status_time),
                            })),
                        };
                    };
                    const deviceRows = devices.map(mapDeviceToRow);
                    dispatch({
                        type: 'SET_DEVICES',
                        payload: {
                            ...data,
                            aaData: deviceRows,
                        },
                    });
                } else {
                    setNotification({
                        message: message,
                        severity: 'error',
                    });
                }
            } catch (error) {
                if (abortController.signal.aborted) return;
                console.error('An unexpected error occurred:', error);
                setNotification({
                    message: t('An unexpected error occurred'),
                    severity: 'error',
                });
            } finally {
                if (latestFetchIdRef.current === fetchId) {
                    dispatch({ type: 'SET_LOADING', payload: false });
                    fetchAbortControllerRef.current = null;
                }
            }
        },
        [
            manageStoreId,
            state.sortModel,
            state.searchQuery,
            state.paginationModel.pageSize,
            state.paginationModel.page,
            state.isProductAssociationDisabled,
            state.isParentMacDisabled,
            formatUtcToStoreTime,
            setNotification,
            t,
        ],
    );

    const clearDevices = (changeStore = false) => {
        const payload = {
            ...searchForm,
            f_mac: '',
            f_product: '',
            f_parent_mac: '',
            f_profile: '',
            f_status_time: '',
            f_last_job_status: '',
            f_update_time: '',
            f_battery_lv: '',
            f_rssi: '',
            f_fw_version: '',
            f_layout: '',
            f_in_rfu: '',
            f_whitelist: assetFeatureRef.current ? '1' : 'All',
        };
        if (changeStore) {
            payload['f_device_type'] = '';
            payload['f_ed_status'] = '';
        }
        setSearchForm(payload);
        dispatch({
            type: 'SET_SEARCH_QUERY',
            payload,
        });
        dispatch({
            type: 'SET_STATUS_DATE_TIME_PICKER_AND_QUERY',
            payload: {
                startTime: null,
                endTime: null,
            },
        });
        dispatch({
            type: 'SET_UPDATE_DATE_TIME_PICKER_AND_QUERY',
            payload: {
                startTime: null,
                endTime: null,
            },
        });
        dispatch({
            type: 'SET_PARENT_MAC_DISABLED',
            payload: false,
        });
        dispatch({
            type: 'SET_PRODUCT_ASSOCIATION_DISABLED',
            payload: false,
        });
        dispatch({
            type: 'SET_PAGINATION_MODEL',
            payload: { page: 0, pageSize: 5 },
        });
        dispatch({
            type: 'SET_SORT_MODEL',
            payload: { field: undefined, sort: null },
        });

        if (!changeStore) {
            fetchDevices(payload);
        }
    };

    const formatToTimestamp = useCallback(
        (startTime: dayjs.Dayjs | null, endTime: dayjs.Dayjs | null) => {
            if (startTime && endTime) {
                const start = storeWallTimeToUtcTimestamp(startTime);
                const end = storeWallTimeToUtcTimestamp(endTime);
                return `${start}_${end}`;
            }
            return '';
        },
        [storeWallTimeToUtcTimestamp],
    );

    const applySearchAndFetch = useCallback(async () => {
        const currentSearchForm = {
            ...searchFormRef.current,
            f_status_time: formatToTimestamp(statusTimeLocal.startTime, statusTimeLocal.endTime),
            f_update_time: formatToTimestamp(updateTimeLocal.startTime, updateTimeLocal.endTime),
        };
        // Update state first, then fetch
        dispatch({ type: 'SET_SEARCH_QUERY', payload: currentSearchForm });
        dispatch({
            type: 'SET_STATUS_DATE_TIME_PICKER_AND_QUERY',
            payload: {
                startTime: statusTimeLocal.startTime,
                endTime: statusTimeLocal.endTime,
            },
        });
        dispatch({
            type: 'SET_UPDATE_DATE_TIME_PICKER_AND_QUERY',
            payload: {
                startTime: updateTimeLocal.startTime,
                endTime: updateTimeLocal.endTime,
            },
        });

        // Fetch with the current search form directly to avoid stale state
        await fetchDevices(currentSearchForm);
    }, [
        fetchDevices,
        formatToTimestamp,
        statusTimeLocal.endTime,
        statusTimeLocal.startTime,
        updateTimeLocal.endTime,
        updateTimeLocal.startTime,
    ]);

    const keyUpHandler = useCallback(
        (event: KeyboardEvent<Element>) => {
            if (event.key === 'Enter') {
                applySearchAndFetch();
            }
        },
        [applySearchAndFetch],
    );

    const handlePushImageConfirm = async ({ imgDataUrl }: { imgDataUrl: string }) => {
        if (isPushing) return;
        setIsPushing(true);
        try {
            const devices = selectedRows as string[];
            // bulk call
            const { data } = await pushImageToDevicesAPI(devices, imgDataUrl);

            if (data?.isSuccess || data?.status === 200) {
                setNotification({
                    message: t('Image push submitted to {{count}} tag(s).', { count: devices.length }),
                    severity: 'success',
                });
            } else {
                setNotification({
                    message: data?.message ?? t('Failed to push image.'),
                    severity: 'error',
                });
            }
            setIsPushImageDialogOpen(false);
        } catch (e) {
            setNotification({
                message: t('An error occurred while pushing the image.'),
                severity: 'error',
            });
        } finally {
            setIsPushing(false);
            fetchDevices();
        }
    };

    const handleSelectionChange = useCallback(
        (newSelection: GridRowSelectionModel) => {
            setSelectedRows(newSelection);
            startTransition(() => {
                const buttons = deriveButtonsStatusFromMeta(newSelection, macMetaMap);
                dispatch({ type: 'SET_BUTTONS_STATUS', payload: buttons });
            });
        },
        [macMetaMap],
    );

    const columnVisibilityModelHandler = useCallback((newModel: GridColumnVisibilityModel) => {
        setColumnVisibilityModel(newModel);
    }, []);

    const errorCodeCellRenderer = useCallback(({ row }: GridRenderCellParams) => {
        const detail = (row.details as DeviceDetail[]).find((d) => d.page_idx === row.current_page);
        return <BaseTableColumn columnValue={detail?.error_code ?? '-'} />;
    }, []);

    const handleRemoveSelected = useCallback(() => {
        if (selectedRows.length === 0) {
            return;
        }
        if (isNALD) {
            setNotification({
                message: t("Some devices aren't available in this store's asset list to remove."),
                severity: 'error',
            });
            return;
        }
        startTransition(() => {
            setIsRemoveDialogOpen(true);
        });
    }, [selectedRows.length, isNALD, setNotification, t]);

    const handleLEDToggle = useCallback(() => {
        if (selectedRows.length === 0) {
            return;
        }
        startTransition(() => {
            setIsLedDialogOpen(true);
        });
    }, [selectedRows.length]);

    const pageChangeHandler = useCallback(
        (pageModel: GridPaginationModel) => {
            if (state.paginationModel.pageSize !== pageModel.pageSize) {
                dispatch({
                    type: 'SET_PAGINATION_MODEL',
                    payload: { ...pageModel, page: 0 },
                });
            } else {
                dispatch({ type: 'SET_PAGINATION_MODEL', payload: pageModel });
            }
        },
        [dispatch, state.paginationModel.pageSize],
    );

    const confirmRemoveDevices = async () => {
        if (!manageStoreId) {
            setNotification({ message: t('Store ID is missing. Please select a store.'), severity: 'error' });
            return;
        }
        if (selectedRows.length === 0) {
            setNotification({ message: t('No devices selected to remove.'), severity: 'warning' });
            return;
        }
        const selectedDeviceList = selectedDevices;
        const tagMacs = selectedDeviceList
            .filter((dev) => dev.device_type_name?.toLowerCase() === 'tag')
            .map((dev) => dev.mac);
        const apRtMacs = selectedDeviceList
            .filter((dev) => {
                const type = dev.device_type_name?.toLowerCase();
                return type === 'ap' || type === 'rt';
            })
            .map((dev) => dev.mac);

        try {
            if (tagMacs.length > 0) {
                const response = await deleteDevicesAPI(manageStoreId, tagMacs);
                if (response.data.status === 200) {
                    // setNotification({
                    //     message: `Successfully removed ${tagMacs.length} tag device(s).`,
                    //     severity: 'success',
                    // });
                } else {
                    setNotification({
                        message: isServiceUnavailable(response.data)
                            ? t(SERVICE_UNAVAILABLE_MESSAGE)
                            : response.data.message || `Failed removing ${tagMacs.length} tag device(s).`,
                        severity: 'error',
                    });
                }
            }

            if (apRtMacs.length > 0) {
                const response = await removeAPorRTDevicesAPI(manageStoreId, apRtMacs);
                if (response.data.status === 200) {
                    // setNotification({
                    //     message: `Successfully removed ${apRtMacs.length} AP/RT device(s).`,
                    //     severity: 'success',
                    // });
                } else {
                    setNotification({
                        message: isServiceUnavailable(response.data)
                            ? t(SERVICE_UNAVAILABLE_MESSAGE)
                            : response.data.message || `Failed removing ${apRtMacs.length} AP/RT device(s).`,
                        severity: 'error',
                    });
                }
            }
        } catch (error) {
            console.error('Error removing devices:', error);
            setNotification({
                message: t('An error occurred while pushing devices.'),
                severity: 'error',
            });
        } finally {
            setIsRemoveDialogOpen(false);
            await fetchDevices();
        }
    };

    const handleAssociateClick = useCallback(
        (mode: AssociationMode) => () => {
            if (isNALD) {
                setNotification({
                    message: t("Some devices aren't available in this store's asset list to perform refresh"),
                    severity: 'error',
                });
                return;
            }
            // Use startTransition to prevent blocking UI when opening dialog
            startTransition(() => {
                setAssociationMode(mode);
            });
        },
        [isNALD, setNotification, t],
    );

    // Memoized handler for AssociateDialog close to prevent lag
    const handleAssociateDialogClose = useCallback(() => {
        startTransition(() => {
            setAssociationMode(null);
        });
    }, []);

    const syncApManagementSwitchChannelModeOnce = useCallback(
        async (options?: {
            keepCurrentDestinationSubChannel?: boolean;
            delayMs?: number;
            shouldManagePolling?: boolean;
        }) => {
            if (selectedApDevices.length !== 1) {
                if (options?.shouldManagePolling || apManagementPollIntervalRef.current !== null) {
                    stopApManagementPolling();
                }
                return null;
            }

            const firstAp = selectedApDevices[0];
            if (!firstAp) return null;

            try {
                const delayMs = options?.delayMs ?? 0;
                if (delayMs > 0) {
                    await new Promise((resolve) => window.setTimeout(resolve, delayMs));
                }
                const detailResp = await getDeviceDetail(String(firstAp.mac));
                const detailData = (detailResp as any)?.data?.data ?? (detailResp as any)?.data ?? {};
                const modeRaw = detailData?.switch_channel_mode;
                const mode =
                    typeof modeRaw === 'string' && modeRaw
                        ? (modeRaw.trim().toUpperCase() as 'ENABLE' | 'DISABLE' | 'PROCESSING')
                        : null;
                const destSub = detailData?.destination_sub_channel;

                if (mode === 'ENABLE' || mode === 'DISABLE' || mode === 'PROCESSING') {
                    setApSwitchChannelModeFromDetail(mode);
                } else {
                    setApSwitchChannelModeFromDetail(null);
                }

                if (!options?.keepCurrentDestinationSubChannel) {
                    if (typeof destSub === 'number') {
                        setApDestinationSubChannel(destSub);
                    } else {
                        setApDestinationSubChannel(null);
                    }
                }

                if (options?.shouldManagePolling || apManagementPollIntervalRef.current !== null) {
                    if (mode === 'PROCESSING') {
                        if (apManagementPollIntervalRef.current === null) {
                            apManagementPollIntervalRef.current = window.setInterval(() => {
                                void syncApManagementSwitchChannelModeOnce({
                                    keepCurrentDestinationSubChannel: true,
                                    shouldManagePolling: true,
                                });
                            }, AP_SWITCH_CHANNEL_POLL_INTERVAL_MS);
                        }
                    } else {
                        stopApManagementPolling();
                    }
                }

                return mode;
            } catch (err) {
                console.error('Failed to fetch AP details for switch_channel_mode:', err);
                return null;
            }
        },
        [selectedApDevices, stopApManagementPolling],
    );

    const handleOpenApManagement = useCallback(async () => {
        if (!isAllApSelected) return;

        setAlertState(null);
        stopApManagementPolling();
        setApSwitchChannelModeFromDetail(null);
        setApDestinationSubChannel(null);

        if (selectedApCount === 1) {
            await syncApManagementSwitchChannelModeOnce({ shouldManagePolling: true });
        }
        setIsApManagementOpen(true);
    }, [isAllApSelected, selectedApCount, stopApManagementPolling, syncApManagementSwitchChannelModeOnce]);

    const handleCloseApManagement = useCallback(() => {
        setAlertState(null);
        stopApManagementPolling();
        setApSwitchChannelModeFromDetail(null);
        setApDestinationSubChannel(null);
        setIsApManagementOpen(false);
    }, [stopApManagementPolling]);

    useEffect(() => {
        if (!isApManagementOpen || selectedApCount !== 1) {
            stopApManagementPolling();
        }
    }, [isApManagementOpen, selectedApCount, stopApManagementPolling]);

    useEffect(() => {
        return () => {
            stopApManagementPolling();
        };
    }, [stopApManagementPolling]);

    const quickActionLabelMap: Record<string, string> = useMemo(
        () => ({
            'wakeup-on': t('Turn On Wake Up Mode'),
            'wakeup-off': t('Turn Off Wake Up Mode'),
            'unlock-ap': t('Unlock AP Network'),
            'lock-ap': t('Lock AP Network'),
            'switch-tag': t('Switch TAG to AP Channel'),
            'timer-start': t('Start'),
            'timer-stop': t('Stop'),
            'timer-force-stop': t('Force Stop'),
        }),
        [t],
    );

    const apQuickActionMap = useMemo<Record<string, { task: string; action: string }>>(
        () => ({
            'wakeup-on': { task: 'wakeup_mode', action: 'start' },
            'wakeup-off': { task: 'wakeup_mode', action: 'stop' },
            'unlock-ap': { task: 'network_mode', action: 'unlock' },
            'lock-ap': { task: 'network_mode', action: 'lock' },
            'switch-tag': { task: 'switch_channel', action: 'start,from,81' },
        }),
        [],
    );

    const runApQuickAction = useCallback(
        async (key: string, actionLabel: string, payload?: { channel?: number }): Promise<boolean> => {
            const config = apQuickActionMap[key];

            if (!selectedApDevices.length) {
                setNotification({ message: t('No devices selected for this action.'), severity: 'warning' });
                return false;
            }
            const scope: 'quick' | 'advanced' =
                key === 'timer-start' || key === 'timer-stop' || key === 'timer-force-stop' ? 'advanced' : 'quick';
            const macs = selectedApDevices.map((device) => device.mac).filter((mac): mac is string => Boolean(mac));
            const results = await Promise.allSettled(
                macs.map((mac) => {
                    const targetChannel = payload?.channel ?? 81;
                    const actionValue =
                        key === 'timer-start'
                            ? `start,from,${targetChannel}`
                            : key === 'timer-stop'
                              ? 'stop'
                              : key === 'timer-force-stop'
                                ? 'stop'
                                : (config?.action ?? '');
                    const taskValue =
                        key === 'timer-start' || key === 'timer-stop' || key === 'timer-force-stop'
                            ? 'switch_channel'
                            : (config?.task ?? '');
                    return infraTaskAPI({ mac, task: taskValue, action: actionValue });
                }),
            );

            const badRequestMessages: string[] = [];
            let otherFailures = 0;
            let successCount = 0;

            results.forEach((result, idx) => {
                if (result.status === 'fulfilled') {
                    const { data, status } = result.value;
                    const apiStatus = data?.status ?? status;

                    if (apiStatus === 400 || data?.isSuccess === false) {
                        const reason =
                            (data as any)?.reason ||
                            (data as any)?.data?.reason ||
                            data?.message ||
                            (data as any)?.data?.message ||
                            t('Action {{action}} failed', { action: actionLabel });
                        badRequestMessages.push(`${macs[idx]}: ${reason}`);
                        return;
                    }

                    if (apiStatus && apiStatus !== 200) {
                        otherFailures += 1;
                        return;
                    }

                    successCount += 1;
                } else {
                    const resp = result.reason?.response;
                    if (resp?.status === 400 || resp?.data?.status === 400) {
                        const reason =
                            resp?.data?.reason ||
                            resp?.data?.data?.reason ||
                            resp?.data?.message ||
                            resp?.data?.data?.message ||
                            t('Action {{action}} failed', { action: actionLabel });
                        badRequestMessages.push(`${macs[idx]}: ${reason}`);
                        return;
                    }
                    otherFailures += 1;
                }
            });

            if (badRequestMessages.length) {
                const separator = badRequestMessages.length > 1 ? '\n' : ' | ';
                setAlertState({
                    message: badRequestMessages.join(separator),
                    severity: 'error',
                    scope,
                });
            } else {
                setAlertState(null);
            }

            if (successCount > 0) {
                setNotification({
                    message: t('Action {{action}} completed', { action: actionLabel }),
                    severity: 'success',
                });
            }

            if (otherFailures > 0) {
                setNotification({
                    message: t('Action {{action}} failed', { action: actionLabel }),
                    severity: 'error',
                });
            }

            if ((badRequestMessages.length > 0 || otherFailures > 0) && isApManagementOpen && selectedApCount === 1) {
                void syncApManagementSwitchChannelModeOnce({ shouldManagePolling: true });
            }

            return badRequestMessages.length === 0 && otherFailures === 0;
        },
        [
            apQuickActionMap,
            isApManagementOpen,
            selectedApCount,
            selectedApDevices,
            setNotification,
            syncApManagementSwitchChannelModeOnce,
            t,
        ],
    );

    const handleQuickAction = useCallback(
        async (key: string, payload?: { channel?: number }) => {
            if (quickActionStatus.status === 'loading') return false;
            const actionLabel = quickActionLabelMap[key] ?? key;
            const shouldShowLoader = !key.startsWith('timer-');
            if (shouldShowLoader) {
                setQuickActionStatus({ key, status: 'loading' });
            }

            try {
                if (
                    apQuickActionMap[key] ||
                    key === 'timer-start' ||
                    key === 'timer-stop' ||
                    key === 'timer-force-stop'
                ) {
                    const ok = await runApQuickAction(key, actionLabel, payload);
                    if (
                        ok &&
                        isApManagementOpen &&
                        selectedApCount === 1 &&
                        (key === 'switch-tag' ||
                            key === 'timer-start' ||
                            key === 'timer-stop' ||
                            key === 'timer-force-stop')
                    ) {
                        stopApManagementPolling();

                        if (key === 'switch-tag') {
                            setApDestinationSubChannel(81);
                        } else if (key === 'timer-start' && typeof payload?.channel === 'number') {
                            setApDestinationSubChannel(payload.channel);
                        }

                        // Render the transitional state immediately, then reconcile from device detail.
                        setApSwitchChannelModeFromDetail('PROCESSING');
                        void syncApManagementSwitchChannelModeOnce({
                            keepCurrentDestinationSubChannel: true,
                            delayMs: 900,
                            shouldManagePolling: true,
                        });
                    }
                    return ok;
                }
            } catch (err) {
                if (isApManagementOpen && selectedApCount === 1) {
                    void syncApManagementSwitchChannelModeOnce({ shouldManagePolling: true });
                }
                setNotification({
                    message: t('Action {{action}} failed', { action: actionLabel }),
                    severity: 'error',
                });
                return false;
            } finally {
                if (shouldShowLoader) {
                    setQuickActionStatus({ key: null, status: 'idle' });
                }
            }

            return false;
        },
        [
            apQuickActionMap,
            isApManagementOpen,
            quickActionLabelMap,
            quickActionStatus.status,
            runApQuickAction,
            selectedApCount,
            setNotification,
            stopApManagementPolling,
            syncApManagementSwitchChannelModeOnce,
            t,
        ],
    );

    const handleConfirmLed = async () => {
        if (isPushing) return;
        setIsPushing(true);
        try {
            let w1 = 0;
            let w2 = 0;
            let w3 = 0;
            let commandString = '';

            if (ledMode === 'blinking') {
                w1 = Number(word1);
                w2 = Number(word2);
                w3 = Number(word3);
                const isValidWord1 = w1 >= 10 && w1 <= 100;
                const isValidWord2 = w2 >= 10 && w2 <= 100;
                const isValidWord3 = w3 >= 1 && w3 <= 3600;
                if (!isValidWord1 || !isValidWord2 || !isValidWord3) {
                    alert(
                        t('Please enter valid LED values:') +
                            '\n' +
                            t('- LED ON Duration: 10–100 ms') +
                            '\n' +
                            t('- LED OFF Duration: 10–100 ms') +
                            '\n' +
                            t('- Blink Time: 1–3600 sec'),
                    );
                    return;
                }

                commandString = `led 1 4 ${w1} ${w2} ${w3}`;
            } else if (ledMode === 'on') {
                commandString = 'led 1 1 0 0';
            } else if (ledMode === 'off') {
                commandString = 'led 1 0 0 0';
            }

            //console.log('Command:', commandString);
            //console.log('Selected devices:', selectedRows);

            const response = await setDevicesToLedMode(selectedRows as string[], commandString as string);
            if (response.data.status === 200) {
                setNotification({
                    message: t('LED mode on devices has been changed.'),
                    severity: 'success',
                });
            } else {
                setNotification({
                    message: t('Failed to change LED mode.'),
                    severity: 'error',
                });
            }
            setIsLedDialogOpen(false);
        } catch (err) {
            setIsLedDialogOpen(false);
        } finally {
            setIsPushing(false);
        }
    };

    useEffect(() => {
        if (isLedDialogOpen) {
            setWord1('');
            setWord2('');
            setWord3('');
            setLedMode('off');
        }
    }, [isLedDialogOpen]);

    useEffect(() => {
        if (ledMode === 'blinking') {
            word1Ref.current?.focus();
        }
    }, [ledMode]);

    const handleDeassociate = async () => {
        if (selectedRows.length === 0) {
            setNotification({
                message: t('No devices selected for deassociation.'),
                severity: 'warning',
            });
            return;
        }

        setIsDeassociating(true);

        try {
            if (selectedRows.length === 1) {
                const mac = selectedRows[0] as string;
                const response = await deassociateSingleDeviceAPI(mac);

                if (response.data.status === 200) {
                    // setNotification({
                    //     message: 'Device successfully deassociated.',
                    //     severity: 'success',
                    // });
                } else {
                    setNotification({
                        message: isServiceUnavailable(response.data)
                            ? t(SERVICE_UNAVAILABLE_MESSAGE)
                            : response.data.message || t('Failed to deassociate the device.'),
                        severity: 'error',
                    });
                }
            } else {
                const response = await deassociateMultipleDevices(selectedRows as string[]);
                if (response.data.status === 200) {
                    // setNotification({
                    //     message: 'Devices successfully deassociated.',
                    //     severity: 'success',
                    // });
                } else {
                    setNotification({
                        message: isServiceUnavailable(response.data)
                            ? t(SERVICE_UNAVAILABLE_MESSAGE)
                            : response.data.message || t('Failed to deassociate devices.'),
                        severity: 'error',
                    });
                }
            }
        } catch {
            setNotification({
                message: t('An error occurred while deassociating devices.'),
                severity: 'error',
            });
        } finally {
            setIsDeassociating(false);
            setIsDeassociateDialogOpen(false);
            await fetchDevices();
        }
    };

    const handleRefreshDevices = async () => {
        if (isNALD) {
            setNotification({
                message: t("Some devices aren't available in this store's asset list to perform associate"),
                severity: 'error',
            });
            return;
        }

        if (isPushing) return;
        setIsPushing(true);

        try {
            if (selectedRows.length === 0) {
                setNotification({
                    message: t('No devices selected for refresh.'),
                    severity: 'warning',
                });
                return;
            }

            if (!manageStoreId) {
                setNotification({ message: t('Store ID is missing. Please select a store.'), severity: 'error' });

                setIsPushing(false);
                return;
            }

            if (!checkTypeAndProduct(selectedDevices)) return;

            const requestData = {
                job_type: 'task',
                command: 'repush',
                devices: selectedRows as string[],
            } as const;

            const { data } = await refreshDevicesAPI(requestData);

            if (data.isSuccess && data.data.allSuccess) {
                setNotification({
                    message: t('Devices refreshed successfully!'),
                    severity: 'success',
                });
            } else {
                setNotification({
                    message: isServiceUnavailable(data)
                        ? t(SERVICE_UNAVAILABLE_MESSAGE)
                        : data.message || t('Failed to refresh devices.'),
                    severity: 'error',
                });
            }
        } catch (error) {
            console.error('Error refreshing devices:', error);
            setNotification({
                message: t('An error occurred while refreshing devices.'),
                severity: 'error',
            });
        } finally {
            setIsPushing(false);
            await fetchDevices();
            // setSelectedProduct(null);
        }
    };

    const checkTypeAndProduct = (devices: DeviceItem[]): boolean => {
        return devices.every((device) => {
            if (device.device_type_name !== 'tag') {
                setNotification({
                    message: t('Only "tag" type devices can be refreshed.'),
                    severity: 'error',
                });
                return false;
            }

            if (!device.details.length) {
                setNotification({
                    message: t("Devices without associated products can't be refreshed."),
                    severity: 'error',
                });
                return false;
            }

            return true;
        });
    };

    const handleTypeFilterChange = useCallback(
        (e: SyntheticEvent, checked: boolean) => {
            const value = (e.target as HTMLInputElement).value;
            const checkedTypes = searchForm.f_device_type !== '' ? searchForm.f_device_type.split(',') : [];

            const updateTypes = checked ? [...checkedTypes, value] : checkedTypes.filter((type) => type !== value);
            const newValue = updateTypes.join(',');

            const updatedSearchForm = { ...searchForm, f_device_type: newValue };
            updateSearchForm('f_device_type', newValue);
            searchFormRef.current = updatedSearchForm;

            startTransition(() => {
                dispatch({ type: 'SET_SEARCH_QUERY', payload: updatedSearchForm });
            });
            fetchDevices(updatedSearchForm);
        },
        [searchForm, updateSearchForm, fetchDevices, dispatch],
    );

    const handleStatusFilterChange = useCallback(
        (e: SyntheticEvent, checked: boolean) => {
            const value = (e.target as HTMLInputElement).value;
            const checkedStatuses = searchForm.f_ed_status !== '' ? searchForm.f_ed_status.split(',') : [];

            const updateStatuses = checked
                ? [...checkedStatuses, value]
                : checkedStatuses.filter((status) => status !== value);
            const newValue = updateStatuses.join(',');

            const updatedSearchForm = { ...searchForm, f_ed_status: newValue };

            updateSearchForm('f_ed_status', newValue);

            searchFormRef.current = updatedSearchForm;

            startTransition(() => {
                dispatch({ type: 'SET_SEARCH_QUERY', payload: updatedSearchForm });
            });
            fetchDevices(updatedSearchForm);
        },
        [searchForm, updateSearchForm, fetchDevices, dispatch],
    );

    const getSortField = useCallback((key: string) => {
        switch (key) {
            case 'product_name':
                return 'details.product_name';
            case 'product_barcode':
                return 'details.product_barcode';
            case 'last_job_status':
                return 'details.last_job_status';
            case 'last_job_status_time':
                return 'details.last_job_status_time';
            case 'layout_id':
                return 'details.layout_name';
            case 'error_code':
                return 'status_code';
            default:
                return key;
        }
    }, []);

    const sortModelChangeHandler = useCallback(
        (sortModel: GridSortModel) => {
            if (sortModel.length > 0) {
                const { field, sort } = sortModel[0];
                const fieldKey = getSortField(field);
                startTransition(() => {
                    dispatch({
                        type: 'SET_SORT_MODEL',
                        payload: { field: fieldKey, sort: sort ?? null },
                    });
                });
            }
        },
        [dispatch, getSortField],
    );

    const productCellRenderer = useCallback(({ row }: GridRenderCellParams, key: keyof DeviceDetail) => {
        if (isEmpty(row.details)) return <BaseTableColumn>-</BaseTableColumn>;

        if (key === 'last_job_status' || key === 'last_job_status_time') {
            const list = row.details.map((o: DeviceDetail) => o[key]) || [];
            const status = uniq(list)[0] ?? '-';

            return (
                <BaseTableColumn>
                    <BaseTableColumnContent columnValue={t(status as string)} />
                </BaseTableColumn>
            );
        }
        if (key === 'product_barcode' || key === 'product_name') {
            return (
                <BaseTableColumn>
                    {row.details.map((detail: DeviceDetail, index: number) => {
                        if (detail.page_idx === 1 || detail.product_id !== null) {
                            return <BaseTableColumnContent key={index} columnValue={(detail[key] as string) || '-'} />;
                        }
                    })}
                </BaseTableColumn>
            );
        }

        return (
            <BaseTableColumn>
                {row.details.map((detail: DeviceDetail, index: number) => (
                    <BaseTableColumnContent
                        key={index}
                        columnValue={utcDateStringToLocalDateFormat(detail[key] as string)}
                    />
                ))}
            </BaseTableColumn>
        );
    }, []);

    const layoutCellRenderer = useCallback(({ row }: GridRenderCellParams) => {
        const layoutItem = row.details.filter(
            (detail: DeviceDetail) => row.current_page === detail.page_idx,
        ) as DeviceDetail[];

        if (layoutItem.length === 0) return <BaseTableColumn columnValue={'-'} />;
        return (
            <BaseTableColumn>
                {layoutItem.map((o, index) => (
                    <BaseTableColumnContent key={index} columnValue={o.layout_name || '-'} />
                ))}
            </BaseTableColumn>
        );
    }, []);

    const simpleCellRenderer = useCallback((param: GridRenderCellParams) => {
        return <BaseTableColumn columnValue={param.value} />;
    }, []);

    const simpleCellRendererWithDefault = useCallback((param: GridRenderCellParams) => {
        return <BaseTableColumn columnValue={t(param.value) || '-'} />;
    }, []);

    const deviceTypeCellRenderer = useCallback((param: GridRenderCellParams) => {
        return <BaseTableColumn columnValue={param.value?.toUpperCase() || '-'} />;
    }, []);

    const batteryCellRenderer = useCallback((param: GridRenderCellParams) => {
        return param.row.device_type_id === 3 ? (
            <BaseTableColumn columnValue={param.value || '-'} />
        ) : (
            <BaseTableColumn columnValue={''} />
        );
    }, []);

    const assetFlagCellRenderer = useCallback((param: GridRenderCellParams) => {
        return (
            <Box
                display="flex"
                alignItems="center"
                height={'100%'}
                sx={{
                    pt: '14px',
                    pb: '13px',
                }}
            >
                {param.value === 0 ? (
                    <CloseIcon color="error" />
                ) : param.value === 1 ? (
                    <CheckIcon color="success" />
                ) : (
                    ''
                )}
            </Box>
        );
    }, []);

    const columns: GridColDef[] = useMemo(
        () => [
            {
                ...GRID_CHECKBOX_SELECTION_COL_DEF,
                hideable: false,
            },
            {
                field: 'mac',
                headerName: t('MAC'),
                flex: 1,
                renderCell: simpleCellRenderer,
            },
            {
                field: 'product_barcode',
                headerName: t('Product Barcode'),
                flex: 1,
                renderCell: (param) => productCellRenderer(param, 'product_barcode'),
            },
            {
                field: 'product_name',
                headerName: t('Product Name'),
                flex: 1,
                renderCell: (param) => productCellRenderer(param, 'product_name'),
            },
            {
                field: 'parent_mac',
                headerName: t('Parent MAC'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'profile_name',
                headerName: t('Profile'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'device_type_name',
                headerName: t('Type'),
                flex: 1,
                renderCell: deviceTypeCellRenderer,
            },
            {
                field: 'ed_status',
                headerName: t('Status'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'ed_status_time',
                headerName: t('Status Time'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'last_job_status',
                headerName: t('Job Status'),
                flex: 1,
                renderCell: (param) => productCellRenderer(param, 'last_job_status'),
            },
            {
                field: 'last_job_status_time',
                headerName: t('Update Time'),
                flex: 1,
                renderCell: (param) => productCellRenderer(param, 'last_job_status_time'),
            },
            {
                field: 'battery_lv',
                headerName: t('Battery Level'),
                flex: 1,
                renderCell: batteryCellRenderer,
            },
            {
                field: 'rssi',
                headerName: t('RSSI'),
                flex: 1,
                renderCell: simpleCellRendererWithDefault,
            },
            {
                field: 'fw_version',
                headerName: t('FW Version'),
                flex: 1,
                renderCell: simpleCellRenderer,
            },
            {
                field: 'layout_id',
                headerName: t('Layout'),
                flex: 1,
                renderCell: (param) => layoutCellRenderer(param),
            },
            {
                field: 'error_code',
                headerName: t('Error Code'),
                flex: 1,
                renderCell: errorCodeCellRenderer,
            },
            {
                field: 'asset_flag',
                headerName: t('Available'),
                flex: 1,
                sortable: false,
                renderCell: assetFlagCellRenderer,
            },
            {
                field: 'action',
                type: 'actions',
                headerName: t('Action'),
                hideable: false,
                disableColumnMenu: false,
                flex: 1,
                getActions: ({ row }) => [
                    <GridActionsCellItem
                        icon={<Visibility />}
                        label="View"
                        sx={{ color: 'secondary.main' }}
                        onClick={() => {
                            setDeviceDetailDialogOpen(true);
                            setViewType(row.device_type_name);
                            setDeviceId(row.mac);
                        }}
                    />,
                ],
            },
        ],
        [
            assetFlagCellRenderer,
            batteryCellRenderer,
            deviceTypeCellRenderer,
            errorCodeCellRenderer,
            layoutCellRenderer,
            productCellRenderer,
            simpleCellRenderer,
            simpleCellRendererWithDefault,
            t,
        ],
    );

    const getRowId = useCallback((row: DeviceItem) => String(row.mac ?? row.device_type_id), []);

    const localeText = useMemo(() => ({ noRowsLabel: t('No devices') }), [t]);

    useEffect(() => {
        return () => {
            fetchAbortControllerRef.current?.abort();
        };
    }, []);

    const syncStoreSettings = useCallback(() => {
        if (!isStoreReady) return;
        if (storeSettings) {
            const { customer_name, asset_list_feature } = storeSettings;
            assetFeatureRef.current = asset_list_feature === '1';
            setShowLedButton(customer_name === 'lotuss-thailand');
        }
    }, [isStoreReady, storeSettings]);

    useEffect(() => {
        const initialize = async () => {
            dispatch({ type: 'SET_LOADING', payload: true });
            syncStoreSettings();
            const baseQuery = {
                ...state.searchQuery,
                f_whitelist: assetFeatureRef.current ? '1' : 'All',
                f_store: manageStoreId != null ? String(manageStoreId) : '',
            };
            switch (from) {
                case 'device-ratio':
                    baseQuery.f_device_type = '3';
                    baseQuery.f_ed_status = 'OFFLINE';
                    setColumnVisibilityModel((prev) => ({
                        ...prev,
                        asset_flag: true,
                    }));
                    break;

                case 'job-ratio':
                    baseQuery.f_device_type = '3';
                    baseQuery.f_last_job_status = `${JobStatusMap.PROCESSING},${JobStatusMap.FAILED}`;
                    setColumnVisibilityModel((prev) => ({
                        ...prev,
                        asset_flag: true,
                    }));
                    break;

                case 'ap-ratio':
                    baseQuery.f_device_type = '2';
                    baseQuery.f_ed_status = 'OFFLINE';
                    break;

                default:
                    break;
            }

            dispatch({ type: 'SET_SEARCH_QUERY', payload: baseQuery });
        };
        initialize();
        return () => {
            window.history.replaceState({}, '');
        };
    }, [from]);

    const isFirstStoreRenderRef = useRef(true);
    useEffect(() => {
        if (isFirstStoreRenderRef.current) {
            isFirstStoreRenderRef.current = false;
            return;
        }
        clearDevices(true);
    }, [manageStoreId]);

    useEffect(() => {
        if (storeDetailsError) {
            dispatch({ type: 'SET_LOADING', payload: false });
            return;
        }
        // Device rows are formatted via formatUtcToStoreTime before being
        // stored (see fetchDevices above), so isTimezoneReady is required
        // here in addition to isStoreReady, unlike pages that only format
        // times at render time.
        if (!isStoreReady || !isTimezoneReady) return;
        fetchDevices();
    }, [isStoreReady, isTimezoneReady, storeDetailsError, state.paginationModel, state.sortModel]);

    useEffect(() => {
        if (isLedDialogOpen) {
            //console.log('EDWINTODO');
        }
    }, [isLedDialogOpen]);

    const [showProfileWarning, setShowProfileWarning] = useState(false);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        mac: true,
        product_barcode: true,
        product_name: true,
        parent_mac: true,
        profile_name: true,
        device_type_name: true,
        ed_status: true,
        ed_status_time: true,
        last_job_status: true,
        last_job_status_time: false,
        battery_lv: false,
        rssi: false,
        fw_version: false,
        layout_id: false,
        error_code: false,
        action: true,
        asset_flag: false,
        // in_rfu: false,
    });

    const tableElement = useMemo(
        () => (
            <BaseDataTable
                rows={state.devices || []}
                columns={columns}
                checkboxSelection
                rowSelectionModel={selectedRows}
                onRowSelectionModelChange={handleSelectionChange}
                isRowSelectable={(params) => (params.row.device_type_name ?? '').toLowerCase() !== 'rt'}
                getRowClassName={(params) =>
                    (params.row.device_type_name ?? '').toLowerCase() === 'rt' ? 'store-rt-row' : ''
                }
                paginationModel={state.paginationModel}
                loading={state.loading}
                paginationMode="server"
                sortingMode="server"
                onSortModelChange={sortModelChangeHandler}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={columnVisibilityModelHandler}
                rowCount={state.rowCount || 0}
                getRowId={getRowId}
                localeText={localeText}
                onPaginationModelChange={pageChangeHandler}
            />
        ),
        [
            state.devices,
            columns,
            selectedRows,
            handleSelectionChange,
            state.paginationModel,
            state.loading,
            sortModelChangeHandler,
            columnVisibilityModel,
            columnVisibilityModelHandler,
            state.rowCount,
            getRowId,
            localeText,
            pageChangeHandler,
        ],
    );

    return (
        <>
            {isPushing && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(0, 0, 0, 0)',
                        zIndex: 2000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                ></div>
            )}
            <BasePaper isFullHeight={false} className="device-page">
                <BaseTitle icon={<SmartScreen />} title={t('Store Device')} />
                {manageStoreId ? (
                    <>
                        <StoreFilterBlock handleSearch={applySearchAndFetch} handleClear={() => clearDevices()}>
                            {columnVisibilityModel.mac && (
                                <BaseTextField
                                    label="MAC"
                                    value={searchForm.f_mac}
                                    onChange={(e) => updateSearchForm('f_mac', e.target.value)}
                                    onKeyUp={keyUpHandler}
                                />
                            )}
                            {(columnVisibilityModel.product_barcode || columnVisibilityModel.product_name) && (
                                <Box className="field-with-checkbox">
                                    <BaseTextField
                                        fullWidth
                                        label={t('Product (Barcode/Name)')}
                                        value={searchForm.f_product}
                                        disabled={state.isProductAssociationDisabled}
                                        onChange={(e) => updateSearchForm('f_product', e.target.value)}
                                        onKeyUp={keyUpHandler}
                                    />
                                    <FormControlLabel
                                        label={t('No Product Association')}
                                        control={
                                            <Checkbox
                                                checked={state.isProductAssociationDisabled}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    dispatch({
                                                        type: 'SET_PRODUCT_ASSOCIATION_DISABLED',
                                                        payload: isChecked,
                                                    });
                                                    updateSearchForm('f_product', '');
                                                }}
                                            />
                                        }
                                    />
                                </Box>
                            )}
                            {columnVisibilityModel.parent_mac && (
                                <Box className="field-with-checkbox">
                                    <BaseTextField
                                        fullWidth
                                        label={t('Parent MAC')}
                                        value={searchForm.f_parent_mac}
                                        onChange={(e) => updateSearchForm('f_parent_mac', e.target.value)}
                                        disabled={state.isParentMacDisabled}
                                        onKeyUp={keyUpHandler}
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={state.isParentMacDisabled}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    dispatch({
                                                        type: 'SET_PARENT_MAC_DISABLED',
                                                        payload: isChecked,
                                                    });
                                                    updateSearchForm('f_parent_mac', '');
                                                }}
                                            />
                                        }
                                        label={t('No Parent MAC')}
                                    />
                                </Box>
                            )}
                            {columnVisibilityModel.profile_name && (
                                <BaseTextField
                                    label={t('Profile')}
                                    value={searchForm.f_profile}
                                    onChange={(e) => updateSearchForm('f_profile', e.target.value)}
                                    onKeyUp={keyUpHandler}
                                />
                            )}
                            {columnVisibilityModel.ed_status_time && (
                                <DateTimeRangePicker
                                    label={t('Status Time Range')}
                                    startTime={statusTimeLocal.startTime}
                                    endTime={statusTimeLocal.endTime}
                                    onChange={(startTime, endTime) => {
                                        // Use startTransition to prevent UI blocking when picking dates
                                        startTransition(() => {
                                            setStatusTimeLocal({ startTime, endTime });
                                        });
                                    }}
                                    onKeyUp={keyUpHandler}
                                />
                            )}
                            {columnVisibilityModel.last_job_status && (
                                <Box className="filter-dropdown">
                                    <FormControl fullWidth onKeyDown={keyUpHandler}>
                                        <BaseSelect
                                            label={t('Job Status')}
                                            multiple
                                            value={searchForm.f_last_job_status?.split(',') || []}
                                            onChange={(e) => {
                                                const rawValues = e.target.value as string[];

                                                if (rawValues.length === 0) {
                                                    updateSearchForm('f_last_job_status', '');
                                                    return;
                                                }
                                                const values = normalizeJobStatuses(rawValues);
                                                updateSearchForm('f_last_job_status', values);
                                            }}
                                            options={jobStatusOptions}
                                            renderValue={(selected) =>
                                                jobStatusOptions
                                                    .filter((opt) => (selected as string[]).includes(opt.value))
                                                    .map((opt) => opt.label)
                                                    .join(', ')
                                            }
                                        />
                                    </FormControl>
                                </Box>
                            )}
                            {columnVisibilityModel.last_job_status_time && (
                                <DateTimeRangePicker
                                    label={t('Update Time Range')}
                                    startTime={updateTimeLocal.startTime}
                                    endTime={updateTimeLocal.endTime}
                                    onChange={(startTime, endTime) => {
                                        // Use startTransition to prevent UI blocking when picking dates
                                        startTransition(() => {
                                            setUpdateTimeLocal({ startTime, endTime });
                                        });
                                    }}
                                    onKeyUp={keyUpHandler}
                                />
                            )}
                            {columnVisibilityModel.battery_lv && (
                                <BaseTextField
                                    label={t('Battery Level')}
                                    value={searchForm.f_battery_lv}
                                    onChange={(e) => updateSearchForm('f_battery_lv', e.target.value)}
                                    onKeyUp={keyUpHandler}
                                />
                            )}
                            {columnVisibilityModel.rssi && (
                                <BaseTextField
                                    label={t('RSSI')}
                                    value={searchForm.f_rssi}
                                    onChange={(e) => updateSearchForm('f_rssi', e.target.value)}
                                    onKeyUp={keyUpHandler}
                                />
                            )}
                            {columnVisibilityModel.fw_version && (
                                <BaseTextField
                                    label={t('FW Version')}
                                    value={searchForm.f_fw_version}
                                    onChange={(e) => updateSearchForm('f_fw_version', e.target.value)}
                                    onKeyUp={keyUpHandler}
                                />
                            )}
                            {columnVisibilityModel.layout_id && (
                                <BaseTextField
                                    label={t('Layout')}
                                    value={searchForm.f_layout}
                                    onChange={(e) => updateSearchForm('f_layout', e.target.value)}
                                    onKeyUp={keyUpHandler}
                                />
                            )}
                            {columnVisibilityModel.asset_flag && !assetFilterDisable && (
                                <Box className="filter-dropdown">
                                    <FormControl fullWidth onKeyDown={keyUpHandler}>
                                        <BaseSelect
                                            label={t('Device Availability')}
                                            value={searchForm.f_whitelist}
                                            onChange={(e) => updateSearchForm('f_whitelist', e.target.value as string)}
                                            options={availabilityOptions}
                                            renderValue={(value) => {
                                                const option = availabilityOptions.find((item) => item.value === value);
                                                return t(option?.label as string);
                                            }}
                                        />
                                    </FormControl>
                                </Box>
                            )}
                        </StoreFilterBlock>
                        <BaseCard className="table-card">
                            <div className="table-box">
                                <div className="actions-box">
                                    <div className="actions-buttons">
                                        <span>
                                            <BaseIconButtonDevice
                                                title={t('Associate')}
                                                icon={<BaseFontIcon className="fa-regular fa-fw fa-tag" />}
                                                color="primary"
                                                onClick={handleAssociateClick(ASSOCIATION_MODE.SINGLE)}
                                                disabled={!state.buttonsStatus.associate}
                                            />
                                        </span>
                                        <span>
                                            <BaseIconButtonDevice
                                                title={`${t('Multiple')}${t('Associate')}`}
                                                icon={<BaseFontIcon className="fa-regular fa-fw fa-tags" />}
                                                color="primary"
                                                onClick={handleAssociateClick(ASSOCIATION_MODE.MULTI)}
                                                disabled={!state.buttonsStatus.multiAssociate}
                                            />
                                        </span>
                                        <span>
                                            <BaseIconButtonDevice
                                                title={t('Deassociate')}
                                                icon={<LinkOff />}
                                                color="primary"
                                                onClick={() => {
                                                    // Use startTransition to prevent blocking UI when opening dialog
                                                    startTransition(() => {
                                                        setIsDeassociateDialogOpen(true);
                                                    });
                                                }}
                                                disabled={!state.buttonsStatus.deassociate}
                                            />
                                        </span>

                                        <span>
                                            <BaseIconButtonDevice
                                                title={t('Refresh')}
                                                icon={<Refresh />}
                                                color="primary"
                                                onClick={handleRefreshDevices}
                                                loading={isPushing}
                                                disabled={!state.buttonsStatus.refresh}
                                            />
                                        </span>

                                        <span>
                                            <BaseIconButtonDevice
                                                title={t('Send Image')}
                                                icon={<ImageIcon />}
                                                color="primary"
                                                onClick={() => {
                                                    const selected = selectedDevices;
                                                    const hasNonTag = selected.some(
                                                        (d) => d.device_type_name?.toLowerCase() !== 'tag',
                                                    );
                                                    if (selected.length === 0 || hasNonTag) {
                                                        setNotification({
                                                            message: t(
                                                                'Please select one or more "tag" devices to push an image.',
                                                            ),
                                                            severity: 'warning',
                                                        });
                                                        return;
                                                    }
                                                    const profiles = new Set(
                                                        selected
                                                            .filter((d) => d.device_type_name?.toLowerCase() === 'tag')
                                                            .map((d) => (d.profile_name ?? '').trim()),
                                                    );
                                                    const mixedProfiles = profiles.size > 1;
                                                    // Use startTransition to prevent blocking UI when opening dialog
                                                    startTransition(() => {
                                                        setIsPushImageDialogOpen(true);
                                                        setShowProfileWarning(mixedProfiles);
                                                    });
                                                }}
                                                disabled={!state.buttonsStatus.sendImage}
                                            />
                                        </span>

                                        <span>
                                            <BaseIconButtonDevice
                                                title={t('AP Management')}
                                                icon={<BaseFontIcon className="fa-solid fa-fw fa-tower-broadcast" />}
                                                color="primary"
                                                onClick={handleOpenApManagement}
                                                disabled={!isAllApSelected}
                                            />
                                        </span>

                                        <span>
                                            <BaseIconButtonDevice
                                                title={t('Remove')}
                                                icon={<Delete />}
                                                color="error"
                                                onClick={handleRemoveSelected}
                                                disabled={!state.buttonsStatus.remove}
                                            />
                                        </span>

                                        {showLedButton && (
                                            <span>
                                                <BaseIconButtonDevice
                                                    title={t('LED')}
                                                    icon={<Lightbulb />}
                                                    color="secondary"
                                                    onClick={handleLEDToggle}
                                                    disabled={!state.buttonsStatus.led}
                                                />
                                            </span>
                                        )}
                                    </div>

                                    <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                                        {columnVisibilityModel.device_type_name && (
                                            <PopoverMenu title={t('Type')} icon={<FilterList />}>
                                                <FormLabel sx={{ pl: 1.5 }}>{t('Type Filter')}</FormLabel>
                                                <FormGroup>
                                                    {typeFilterOptions.map((option) => (
                                                        <FormControlLabel
                                                            key={option.label}
                                                            control={<Checkbox />}
                                                            checked={searchForm.f_device_type.includes(
                                                                option.value.toString(),
                                                            )}
                                                            label={option.label}
                                                            value={option.value}
                                                            onChange={handleTypeFilterChange}
                                                            sx={{ m: 0 }}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </PopoverMenu>
                                        )}
                                        {columnVisibilityModel.ed_status && (
                                            <PopoverMenu title={t('Status')} icon={<FilterList />}>
                                                <FormLabel sx={{ pl: 1.5 }}>{t('Status Filter')}</FormLabel>
                                                <FormGroup>
                                                    {statusFilterOptions.map((option) => (
                                                        <FormControlLabel
                                                            key={option}
                                                            control={<Checkbox />}
                                                            checked={searchForm.f_ed_status.includes(option)}
                                                            label={option}
                                                            value={option}
                                                            onChange={handleStatusFilterChange}
                                                            sx={{ m: 0 }}
                                                        />
                                                    ))}
                                                </FormGroup>
                                            </PopoverMenu>
                                        )}
                                    </Box>
                                </div>

                                {tableElement}
                            </div>
                        </BaseCard>
                    </>
                ) : (
                    <NoStoreSelectionBlock />
                )}

                <APDialog
                    open={isApManagementOpen}
                    count={selectedApCount}
                    duplicateSubChannels={apSubChannelConsensus.duplicateValues}
                    onQuickAction={handleQuickAction}
                    quickActionLoadingKey={quickActionStatus.status === 'loading' ? quickActionStatus.key : null}
                    alertState={alertState}
                    onClearAlert={() => setAlertState(null)}
                    initialSwitchChannelMode={apSwitchChannelModeFromDetail ?? apSwitchChannelMode}
                    initialDestinationSubChannel={apDestinationSubChannel}
                    onClose={() => {
                        startTransition(() => {
                            handleCloseApManagement();
                        });
                    }}
                />

                <BaseDialog
                    dialogTitle={t('Remove Devices')}
                    confirmButtonText={t('CONFIRM')}
                    cancelButtonText={t('CANCEL')}
                    onConfirm={confirmRemoveDevices}
                    onClose={() => {
                        startTransition(() => {
                            setIsRemoveDialogOpen(false);
                        });
                    }}
                    open={isRemoveDialogOpen}
                >
                    {t('Are you sure you want to remove the {{count}} devices?', { count: selectedRows.length })}
                </BaseDialog>

                {deviceDetailDialogOpen && (
                    <DeviceDetailDialog
                        open={deviceDetailDialogOpen}
                        deviceId={deviceId}
                        type={viewType as 'ap' | 'rt' | 'tag'}
                        formatUtcToStoreTime={formatUtcToStoreTime}
                        onClose={() => {
                            setDeviceDetailDialogOpen(false);
                            setViewType('');
                        }}
                    />
                )}

                <BaseDialog
                    open={isLedDialogOpen}
                    dialogTitle={t('LED Mode')}
                    confirmButtonText={t('Send')}
                    cancelButtonText={t('Cancel')}
                    onConfirm={handleConfirmLed}
                    onClose={() => {
                        startTransition(() => {
                            setIsLedDialogOpen(false);
                        });
                    }}
                >
                    <Box
                        sx={{
                            width: 500,
                            height: 160,
                            maxWidth: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0,
                        }}
                    >
                        <Typography variant="caption">
                            {t('Use this panel to change the LED behavior — not to view its current status.')}
                        </Typography>
                        <FormControl>
                            <RadioGroup row value={ledMode} onChange={(e) => setLedMode(e.target.value)}>
                                <FormControlLabel value="off" control={<Radio />} label={t('OFF')} />
                                <FormControlLabel value="on" control={<Radio />} label={t('Always ON')} />
                                <FormControlLabel value="blinking" control={<Radio />} label={t('Blinking')} />
                            </RadioGroup>
                        </FormControl>

                        {ledMode === 'blinking' && (
                            <>
                                <Grid container spacing={2}>
                                    <Grid item xs={4}>
                                        <Typography>{t('LED ON Duration')}</Typography>
                                        <BaseTextField
                                            inputRef={word1Ref}
                                            type="number"
                                            fullWidth
                                            placeholder="e.g. 50"
                                            // InputProps={{ endAdornment: <Typography>ms</Typography> }}
                                            endIcon={<Typography>{t('ms')}</Typography>}
                                            value={word1}
                                            onChange={(e) => setWord1(e.target.value)}
                                            inputProps={{ min: 10, max: 100 }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography>{t('LED OFF Duration')}</Typography>
                                        <BaseTextField
                                            type="number"
                                            fullWidth
                                            placeholder="e.g. 50"
                                            // InputProps={{ endAdornment: <Typography>ms</Typography> }}
                                            endIcon={<Typography>{t('ms')}</Typography>}
                                            value={word2}
                                            onChange={(e) => setWord2(e.target.value)}
                                            inputProps={{ min: 10, max: 100 }}
                                        />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <Typography>{t('Total Blink Time')}</Typography>
                                        <BaseTextField
                                            type="number"
                                            fullWidth
                                            placeholder="e.g. 10"
                                            // InputProps={{ endAdornment: <Typography>sec</Typography> }}
                                            endIcon={<Typography>{t('s')}</Typography>}
                                            value={word3}
                                            onChange={(e) => setWord3(e.target.value)}
                                            inputProps={{ min: 1, max: 3600 }}
                                        />
                                    </Grid>
                                </Grid>

                                <Typography variant="caption" color="textSecondary">
                                    {t(
                                        'LED will blink ON for {{on}} ms, OFF for {{off}} ms, repeated for {{secs}} seconds.',
                                        {
                                            on: word1 || '...',
                                            off: word2 || '...',
                                            secs: word3 || '...',
                                        },
                                    )}
                                </Typography>
                            </>
                        )}
                    </Box>
                </BaseDialog>

                <BaseDialog
                    open={isDeassociateDialogOpen}
                    dialogTitle={t('Confirm Deassociation')}
                    confirmButtonText={isDeassociating ? <CircularProgress size={20} color="inherit" /> : t('Confirm')}
                    cancelButtonText={t('Cancel')}
                    onConfirm={handleDeassociate}
                    onClose={() => {
                        startTransition(() => {
                            setIsDeassociateDialogOpen(false);
                        });
                    }}
                    disableConfirm={isDeassociating}
                >
                    {t('Are you sure you want to deassociate {{count}} device(s)?', { count: selectedRows.length })}
                </BaseDialog>

                <PushImageDialog
                    open={isPushImageDialogOpen}
                    onClose={() => {
                        startTransition(() => {
                            setIsPushImageDialogOpen(false);
                        });
                    }}
                    onConfirm={handlePushImageConfirm}
                    selectedCount={selectedRows.length}
                    isPushing={isPushing}
                    showProfileWarning={showProfileWarning}
                />
                <AssociationProvider>
                    <AssociateDialog
                        mode={associationMode}
                        onUpdateSuccess={async () => {
                            setAssociationMode(null);
                            await fetchDevices();
                        }}
                        onClose={handleAssociateDialogClose}
                        macs={selectedRows as string[]}
                    />
                </AssociationProvider>
            </BasePaper>
        </>
    );
}
